import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { commenterLogout, fetchCommenterMe } from "../lib/commenter";

/**
 * Read-only context for the currently signed-in Google commenter.
 * Distinct from `AuthContext` (which represents the admin email/password
 * session). A visitor can be:
 *   • signed out (most readers)
 *   • signed in as a commenter (can post comments)
 *   • signed in as an admin (can post comments AND moderate)
 * Both auths can coexist — different cookies.
 */
const CommenterContext = createContext({
  user: null,
  loading: true,
  refresh: async () => {},
  logout: async () => {},
});

export function CommenterProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const u = await fetchCommenterMe();
      setUser(u);
    } catch (_) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If we just got back from OAuth, the session_id processing happens
    // in AuthCallback — skip the /me check here so the cookie has a
    // chance to land first (see playbook race-condition fix).
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await commenterLogout();
    } catch (_) {
      /* ignore */
    }
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, refresh, logout }),
    [user, loading, refresh, logout],
  );

  return (
    <CommenterContext.Provider value={value}>
      {children}
    </CommenterContext.Provider>
  );
}

export const useCommenter = () => useContext(CommenterContext);
